<template>
  <NuxtLink
    :to="toPath"
    :target="linkTarget"
    class="experience-card border-curve"
    :class="{ vertical: vertical }"
    :style="{ width: cardWidth }"
  >
    <div class="inner">
      <NuxtImg loading="lazy"
        v-no-right-click
        :src="imageSrc"
        @error="onImageError"
        :alt="title"
      />
      <div class="content card-title grow">
        <div>
          <div v-if="hasDiscount && offPercent > 0" class="discount-percent">
            {{ t("save") }}
            {{ offPercent }}%
          </div>
          <div :class="hasDiscount ? 'mt-[8px]' : 'md:mt-5 mt-0.5'">
            <div class="flex" :class="{ 'md:justify-between': !vertical }">
              <div v-if="rate > 0" class="flex gap-1 items-center">
                <span v-for="index in 5" :key="index" class="rating-star-item">
                  <icon-star-1
                    class="star text-transparent text-[16px] md:text-[20px]"
                    :class="{
                      'filled !text-star-color': rate >= index,
                      'half-filled !text-star-color':
                        rate > index - 1 && rate < index,
                    }"
                  />
                </span>
              </div>
            </div>
            <h3
              :title="title"
              class="md:text-heading-3 text-mobile-heading-3 text-gray-1"
              :class="titleClass ? titleClass : ''"
            >
              <span :class="{ 'line-clamp-3': vertical }">
                {{ title }}
              </span>
              <LazyExperienceCardPayLater v-if="hasPayLater" has-background />
            </h3>
          </div>
        </div>
        <div class="relative flex">
          <div class="description">
            <span v-if="!vertical">
              {{ shortDescription }}
            </span>

            <span v-if="vertical && shortDescription">
              {{ shortDescription?.split("").slice(0, 68).join("") }}...
            </span>
          </div>
        </div>
      </div>
      <!-- <div class="md:w-[560px]"> -->
      <div class="py-5" :class="{ 'md:w-3/6': !vertical }">
        <div class="rating-and-price relative">
          <div class="experience-arrow">
            <icon-arrow-up-2 class="rtl:-rotate-90" />
          </div>
          <div
            class="flex items-end gap-2 absolute bottom-3 md:bottom-0 max-md:ltr:left-5 max-md:rtl:right-5"
            :class="[
              { 'md:flex-col ': !vertical },
              { 'md:ltr:left-8 md:rtl:right-8': vertical },
            ]"
          >
            <div
              class="price flex flex-col items-end gap-1"
              :class="{ discount: offPercent > 0 }"
            >
              <div v-if="!isNegotiable" class="flex w-full gap-1">
                <div>
                  <span
                    class="md:text-heading-5 md:font-extrabold text-primary-2"
                    :class="{ 'max-md:hidden': offPercent == 0 }"
                  >
                    {{ t("from").toLowerCase() }}
                  </span>
                </div>
                <div>
                  <span
                    v-if="offPercent > 0"
                    class="real-price"
                    :class="{
                      '!text-paragraph-2': offPercent > 0,
                    }"
                  >
                    {{ formatPrice(marketPrice) }}
                  </span>
                </div>
              </div>
              <div class="title">
                <div v-if="!isNegotiable" :class="{ 'md:hidden': !vertical }">
                  <span class="text-heading-5 font-extrabold text-primary-2">
                    {{ t("from") }}
                  </span>
                </div>
                <div>
                  <span class="final-price">
                    {{
                      isNegotiable
                        ? t("requestDetails")
                        : formatPrice(price) + " " + currency
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- </div> -->
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import type { PropType } from "vue";
import type { ICity } from "../../types/general";
import { formatPrice, offPercentCalculator } from "../../composables/helper";
import { watch } from "vue";

const { t } = useI18n();

const props = defineProps({
  width: { type: Number, required: false, default: null },
  image: { type: String, required: true },
  cities: { type: Array as PropType<ICity[]>, required: true },
  title: { type: String, required: true },
  rate: { type: Number, default: null },
  marketPrice: { type: Number, required: false, default: null }, //price before off - if product has off
  price: { type: Number, required: true, default: null },
  currency: { type: String, required: true },
  toPath: { type: String, default: "/" },
  slug: { type: String, required: true },
  shortDescription: { type: String, default: null },
  vertical: { type: Boolean, default: false },
  titleClass: { type: String, default: null },
  isNegotiable: { type: Boolean, default: false },
  hasPayLater: { type: Boolean, default: false },
});

const offPercent = ref(offPercentCalculator(props.marketPrice, props.price));

const cardWidth = computed(() => {
  return props.width ? props.width + "px" : null;
});

const hasDiscount = computed(() => {
  if (props.marketPrice && props.marketPrice > props.price) return true;
  return false;
});
const isDesktop = ref(false);
const handleResize = () => {
  isDesktop.value = window.innerWidth >= 1024;
};
onMounted(() => {
  handleResize();
});
const linkTarget = computed(() => {
  return isDesktop.value ? "_blank" : "_self";
});

const fallbackImage = "product-default.jpg";
const imageSrc = ref(props.image);

const onImageError = () => {
  imageSrc.value = fallbackImage;
};

watch(
  () => props.image,
  (newSrc) => {
    imageSrc.value = newSrc;
  }
);
</script>

<style lang="scss" scoped>
.experience-card {
  @apply relative bg-gray-4 rounded-[25px] overflow-hidden block md:flex md:max-h-[280px]
  max-w-96 md:max-w-[1000px] mx-auto;
}
.experience-card.dark-mode {
  .card-title .city,
  .rating-and-price .rate-text,
  .rating-and-price .price .title,
  .content .description {
    @apply text-gray-5;
  }
  .card-title h5 {
    @apply text-white;
  }
}
.experience-card.vertical {
  @apply block max-h-full;
}
.experience-card .inner {
  @apply relative flex flex-col md:flex-row gap-2 w-full md:ltr:pr-5 md:rtl:pl-5;
}
.experience-card.vertical .inner {
  @apply flex-col px-0 h-full justify-between;
}

.experience-card img {
  @apply w-full md:w-[287px] object-cover xs:h-[170px] sm:min-h-[230px] md:h-[280px] md:max-h-[280px]
    md:min-w-[287px];
}
.experience-card.vertical img {
  @apply w-full h-[170px] min-w-full;
}

.experience-card .content {
  @apply p-5 pt-0 max-md:h-auto flex-col md:justify-between;
}
.experience-card .content .description {
  @apply md:w-fit md:h-auto text-ellipsis md:line-clamp-3 line-clamp-6 text-gray-1 mt-5
  text-mobile-body-text md:text-body-text;
}
.experience-card.vertical .content {
  @apply h-[11.5rem];
}
.experience-card.vertical .content .description {
  @apply w-fit h-[5.5rem] line-clamp-4;
}

.experience-card .card-title {
  @apply flex flex-col mb-0.5 max-md:text-[20px];
}
.experience-card .card-title h5 {
  @apply text-[20px] font-extrabold md:text-heading-3 text-gray-1 text-ellipsis mb-4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.experience-card.vertical .card-title h5 {
  @apply text-heading-4;
}
.experience-card .rating-and-price {
  @apply flex gap-1 justify-between h-full md:flex-col flex-row-reverse px-5 md:px-0 items-end md:min-w-[110px];
}

.experience-card.vertical .rating-and-price {
  @apply flex-row-reverse px-8;
}

.experience-card .rating-and-price .rate-text {
  @apply text-gray-1 xs:text-mobile-paragraph-1 lg:text-paragraph-1;
}

.experience-card .rating-and-price .price .title {
  @apply xs:text-mobile-heading-4 lg:text-heading-4 text-primary-2 whitespace-nowrap
     flex md:flex-col flex-row items-center justify-start w-full gap-1;
}
.experience-card.vertical .rating-and-price .price .title {
  @apply flex-row;
}

.experience-card .rating-and-price .price .final-price {
  @apply whitespace-nowrap text-primary-2
    text-mobile-heading-4 md:text-heading-5 lg:text-heading-4 font-extrabold;
}

.experience-card .rating-and-price .price.discount {
  @apply xs:max-sm:flex xs:max-sm:flex-col xs:max-sm:justify-start;
}

.experience-card .rating-and-price .price.discount .title {
  @apply text-paragraph-2;
}

.experience-card .rating-and-price .price .real-price {
  @apply mr-1.5 relative text-heading-5 text-primary-2 line-through;
}

.experience-card .discount-percent {
  @apply bg-discount-background text-discount-color rounded-[5px] pr-[15px] pl-[18px] py-2 w-fit text-mobile-heading-5 md:text-heading-5  md:relative mt-3;
}
.experience-card.vertical .discount-percent {
  @apply absolute top-1;
}

.star.half-filled {
  clip-path: inset(0 50% 0 0);
}
html[dir="rtl"] .star.half-filled {
  clip-path: inset(0 0 0 50%);
}
.experience-arrow {
  @apply transition-all duration-[400ms] bg-primary-2-lighter text-primary-2 w-[40px] h-[40px] rounded-full flex items-center justify-center;
}
.experience-card:hover .experience-arrow {
  @apply bg-primary-2 text-white transition-all duration-[400ms];
}
</style>
